// src/components/FormResult.tsx
//
// ★★ ONE way a form says what happened, because eleven copies of it said it
// SILENTLY.
//
// ══ THE DEFECT THIS CLOSES, AND WHY IT COUNTS AS A DEFECT ══
//
// Every administration screen in this product rendered its outcome as a bare
// paragraph:
//
//     {form.status === 'error' && <p className="…danger…">{form.message}</p>}
//
// That is invisible to a screen reader. Nothing about a `<p>` appearing in the
// DOM is announced: the officer presses Save, focus stays on the button, and
// the sentence that says whether a ward class was superseded or refused is
// painted where they are not looking. `docs/delivery/DEMO-READINESS-PLAN.md`
// §Wave 5 records it as the one real accessibility gap its audit found —
// **WCAG 2.2 3.3.1 Error Identification** — across eleven screens.
//
// ⚠ WCAG 2.1 AA is OUR commitment, not a tender requirement: `WCAG` appears
// zero times in the entire pack (ENGINEERING.md §2). It is written that way here
// rather than cited as an obligation the Authority imposed.
//
// ══ WHY A COMPONENT AND NOT ELEVEN `role="alert"` ATTRIBUTES ══
//
// Because the attribute is the easy half and the wrong half is which one. An
// alert INTERRUPTS whatever is speaking; a status waits its turn. A refusal
// must interrupt — the officer's change did not happen and they are about to
// walk away believing it did. A success must not — they asked for it, they are
// expecting it, and interrupting a screen-reader user mid-sentence to tell them
// something went right is how a product teaches people to switch announcements
// off. Two behaviours, one decision, made once here rather than eleven times.
//
// ⚠ **`aria-atomic` so the WHOLE sentence is re-read.** Without it a live
// region announces only the changed nodes, and a message that changes from one
// refusal to another can be read out as a fragment that means nothing.
//
// ⚠ **The element is always MOUNTED, and only its CONTENT changes.** A live
// region that is inserted into the DOM at the same moment as its text is
// unreliable across screen readers — several only announce changes to a region
// they were already observing. That is why this renders an empty container in
// the idle state rather than `null`, which is the one thing about a live region
// a reader will not guess from the markup.
import { useEffect, useRef } from 'react'
import { plainProse } from '../lib/plain-words'

export type FormResultState =
  | { readonly status: 'idle' }
  | { readonly status: 'submitting' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'success'; readonly message: string }

export interface FormResultProps {
  readonly state: FormResultState
  /**
   * Extra classes for the layout the calling form uses — these screens are
   * two-column grids and the message spans both.
   */
  readonly className?: string
  /**
   * ★ F-03: move keyboard focus to the message when it appears. Use it where the control that was pressed has
   * since left the page or become disabled (a row's Remove, a Save that is now clean), so focus is not dropped to
   * the top of the document. The message is a live region AND the focus target, so it is read once on arrival.
   */
  readonly focusOnResult?: boolean
}

export function FormResult({ state, className, focusOnResult = false }: FormResultProps) {
  const failed = state.status === 'error'
  const message = state.status === 'error' || state.status === 'success' ? state.message : ''
  const alertRef = useRef<HTMLParagraphElement>(null)
  const statusRef = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (!focusOnResult) return
    if (state.status === 'error') alertRef.current?.focus()
    else if (state.status === 'success') statusRef.current?.focus()
  }, [focusOnResult, state.status, message])

  return (
    <div className={className}>
      {/*
        ⚠ TWO regions, not one with a swapped `role`. Changing the role of a
        live region while it is mounted is not reliably picked up — the
        assistive technology bound to it when it was inserted. Rendering both
        and putting the text in the right one keeps each region's politeness
        fixed for its whole life, which is what makes the announcement happen.
      */}
      <p
        ref={alertRef}
        tabIndex={-1}
        role="alert"
        aria-atomic="true"
        className={`text-sm m-0 text-[var(--sgds-danger-color-emphasis)] ${failed ? '' : 'sr-only'}`}
      >
        {failed ? plainProse(message, true) : ''}
      </p>
      <p
        ref={statusRef}
        tabIndex={-1}
        role="status"
        aria-atomic="true"
        className={`text-sm m-0 text-[var(--sgds-success-color-emphasis)] ${state.status === 'success' ? '' : 'sr-only'}`}
      >
        {state.status === 'success' ? plainProse(message, true) : ''}
      </p>
    </div>
  )
}
